/**
 * RefQuantityGroupNode — custom React Flow node for grouped reference quantities.
 *
 * Shows a compact card listing each ref quantity (e.g. "wing_area = 6 m²").
 * Visually distinct from sweep groups (orange gradient vs green).
 *
 * - Click a quantity row to open the edit modal.
 * - "Auto-detect" footer triggers wing area detection from a surface mesh.
 * - A mesh icon (◆) marks quantities defined by geometry/mesh analysis;
 *   a pencil icon (✎) marks manually entered values.
 */

import { memo, useState, useId } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import type { DAGNode } from "../../api/artifacts";
import type { RefQuantityGroup } from "./dagGrouping";
import { CrispNodeContent } from "./CrispNodeContent";

interface RefQuantityGroupData {
  label: string;
  group: RefQuantityGroup;
  labels: string[];
  expanded: boolean;
  onToggle?: () => void;
  onSelectNode?: (nodeId: string) => void;
  onEditQuantity?: (quantity: DAGNode, geometryId: string | null) => void;
  onAutoDetect?: (meshNode: DAGNode, geometryId: string) => void;
  [key: string]: unknown;
}

function SourceBadge({ quantity, group }: { quantity: DAGNode; group: RefQuantityGroup }) {
  const isGeomDefined = group.geometryDefinedIds.has(quantity.id);
  const source = quantity.source as string | null | undefined;

  if (isGeomDefined) {
    const tip = source ? `Defined by geometry (${source})` : "Defined by geometry";
    return (
      <span title={tip} style={{
        fontSize: 8, opacity: 0.9, marginLeft: 4,
        background: "rgba(34,197,94,0.3)", borderRadius: 3,
        padding: "0 3px", lineHeight: "14px", flexShrink: 0,
      }}>
        ◆ mesh
      </span>
    );
  }

  return (
    <span title="Manually entered" style={{
      fontSize: 8, opacity: 0.6, marginLeft: 4,
      background: "rgba(255,255,255,0.1)", borderRadius: 3,
      padding: "0 3px", lineHeight: "14px", flexShrink: 0,
    }}>
      ✎ manual
    </span>
  );
}

const MAX_VISIBLE = 5;
const ROW_H = 22;

function RefQuantityGroupNodeInner({ data }: NodeProps) {
  const { label, labels, group, expanded, onToggle, onEditQuantity, onAutoDetect } =
    data as unknown as RefQuantityGroupData;

  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [meshMenuOpen, setMeshMenuOpen] = useState(false);
  const scrollId = useId();

  const hasMeshes = group.surfaceMeshNodes.length > 0;
  const count = group.quantities.length;

  if (!expanded) {
    return (
      <>
        <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
        <CrispNodeContent width="100%" height="100%">
          <div
            style={{ position: "relative", width: "100%", cursor: "pointer" }}
            onClick={onToggle}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10, opacity: 0.8 }}>⚙</span>
                <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
              </div>
              <span style={{ fontSize: 12, opacity: 0.7 }}>▾</span>
            </div>
          </div>
        </CrispNodeContent>
        <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      </>
    );
  }

  const needsScroll = count > MAX_VISIBLE;

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <CrispNodeContent width="100%" height="100%">
      <div style={{ position: "relative", width: "100%" }}>
        {needsScroll && (
          <style>{`
            [data-scroll-id="${scrollId}"]::-webkit-scrollbar { width: 4px; }
            [data-scroll-id="${scrollId}"]::-webkit-scrollbar-track { background: transparent; }
            [data-scroll-id="${scrollId}"]::-webkit-scrollbar-thumb {
              background: rgba(255,255,255,0.35); border-radius: 2px;
            }
          `}</style>
        )}
        <div
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, cursor: "pointer" }}
          onClick={onToggle}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10, opacity: 0.8 }}>⚙</span>
            <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
          </div>
          <span style={{ fontSize: 12, opacity: 0.7 }}>▴</span>
        </div>

        <div
          data-scroll-id={scrollId}
          className={needsScroll ? "nowheel" : undefined}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxHeight: needsScroll ? MAX_VISIBLE * ROW_H : undefined,
            overflowY: needsScroll ? "auto" : "visible",
            overflowX: "hidden",
          }}
        >
          {labels.map((lbl, i) => {
            const qty = group.quantities[i];
            return (
            <span
              key={i}
              role="button"
              tabIndex={0}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              onClick={(e) => {
                e.stopPropagation();
                if (qty && onEditQuantity) onEditQuantity(qty, group.geometryId);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  if (qty && onEditQuantity) onEditQuantity(qty, group.geometryId);
                }
              }}
              style={{
                fontSize: 10,
                background: hoveredIdx === i ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.2)",
                borderRadius: 3,
                padding: "2px 6px",
                whiteSpace: "nowrap",
                cursor: onEditQuantity ? "pointer" : "default",
                transition: "background 0.15s",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{lbl}</span>
              {qty && <SourceBadge quantity={qty} group={group} />}
            </span>
            );
          })}
        </div>

        {hasMeshes && onAutoDetect && (
          <div style={{ marginTop: 6, position: "relative" }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (group.surfaceMeshNodes.length === 1) {
                  onAutoDetect(group.surfaceMeshNodes[0]!, group.geometryId!);
                } else {
                  setMeshMenuOpen((prev) => !prev);
                }
              }}
              style={{
                width: "100%",
                padding: "3px 6px",
                fontSize: 9,
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: 4,
                background: "rgba(255,255,255,0.15)",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              Auto-detect from mesh
            </button>

            {meshMenuOpen && group.surfaceMeshNodes.length > 1 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 20,
                  background: "var(--color-surface, #1a1a2e)",
                  border: "1px solid var(--gray-a5, #444)",
                  borderRadius: 4,
                  marginTop: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                }}
              >
                {group.surfaceMeshNodes.map((mesh) => (
                  <button
                    key={mesh.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMeshMenuOpen(false);
                      onAutoDetect(mesh, group.geometryId!);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "4px 8px",
                      fontSize: 10,
                      background: "transparent",
                      border: "none",
                      color: "var(--gray-12, #eee)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.background = "var(--gray-a3, rgba(255,255,255,0.1))";
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.background = "transparent";
                    }}
                  >
                    {mesh.name || String(mesh.path).split(".").pop()}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </CrispNodeContent>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </>
  );
}

export const RefQuantityGroupNode = memo(RefQuantityGroupNodeInner);
export default RefQuantityGroupNode;
